import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  IconButton,
  FormControlLabel,
  Checkbox,
  DialogActions,
  Dialog,
  DialogTitle,
  DialogContent,
  ToggleButtonGroup,
  ToggleButton,
  Alert,
  Divider,
} from '@mui/material';
import { useDataProvider, useNotify, List, Datagrid, TextField as RaTextField, Pagination } from 'react-admin';
import {
  CleanupRequestPayload,
  CleanupRule,
  CleanupRulePayload,
  CleanupTargetField,
  ClientCleanupFormProps,
  RuleEntry,
} from '../utils/DataTypes';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import ClientCrosswalkSummaryTable from './ClientCrosswalkSummaryTable';
import { validateCustomSql } from './clientSplitHelpers';

const ClientCleanupForm = ({ primaryClient, effectiveDate, onSaveRule, isSaving }: ClientCleanupFormProps) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();

  const [listFilters, setListFilters] = useState<any | null>(null);
  const [financialCutoffDate, setFinancialCutoffDate] = useState('');

  const [hasLoadedPreviewList, setHasLoadedPreviewList] = useState(false);
  const [previewAfterTable, setPreviewAfterTable] = useState<any[]>([]);

  const [isRuleDialogOpen, setIsRuleDialogOpen] = useState(false);
  const [editingRuleIndex, setEditingRuleIndex] = useState<number | null>(null);
  const [draftRule, setDraftRule] = useState<CleanupRule | null>(null);

  const [ruleEntryMode, setRuleEntryMode] = useState<RuleEntry>('ui');
  const hasMatchValue = !!draftRule?.matchValue?.trim();

  const hasBothTrims = Number(draftRule?.leftTrim || 0) > 0 && Number(draftRule?.rightTrim || 0) > 0;

  const isRemove = draftRule?.trimAction === 'REMOVE';

  const leftLabel = isRemove ? 'Remove from Left (chars)' : 'Take Left (chars)';

  const rightLabel = isRemove ? 'Remove from Right (chars)' : 'Take Right (chars)';

  const canUseRemainder = draftRule?.mode != 'swap' && draftRule?.fieldToUpdate === 'location_number';

  const customSqlError =
    ruleEntryMode === 'custom_sql' && draftRule?.customSql ? validateCustomSql(draftRule.customSql ?? '') : null;

  const DEFAULT_CUSTOM_SQL = `UPDATE {{TABLE}} 
  SET location_number = ' ' 
  WHERE client_number='12345';`;

  const clearTransformedPreview = () => {
    setPreviewAfterTable([]);
  };

  const [rules, setRules] = useState<CleanupRule[]>([]);

  const hasUiRules = rules.some((r) => r.ruleEntryMode === 'ui');

  const fieldLabel = (field?: string) => {
    switch (field) {
      case 'medical_group_number':
        return 'Medical';
      case 'location_number':
        return 'Location';
      case 'division_number':
        return 'Division';
      default:
        return 'Field';
    }
  };

  const CleanupEmpty = () => (
    <Box sx={{ p: 6, textAlign: 'center' }}>
      <Typography variant="h6" paragraph color="text.secondary">
        No Preview Data Found
      </Typography>
      <Typography variant="body2" color="text.secondary">
        There are no records matching your selected client and date.
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Try adjusting the "Financial Cutoff Date (From)" to an earlier date.
      </Typography>
    </Box>
  );

  const handleFetchPreview = () => {
    setListFilters({
      client_number: primaryClient.client_number,
      financial_cutoff_date: financialCutoffDate,
    });
    setHasLoadedPreviewList(true);
  };

  const handleSave = () => {
    if (rules.length === 0) {
      notify('notifications.CLIENT_CLEANUP.RULES.ADD_ONE_RULE', { type: 'warning' });
      return;
    }

    for (let i = 0; i < rules.length; i++) {
      const r = rules[i];

      if (r.ruleEntryMode === 'custom_sql') {
        if (!r.customSql?.trim()) {
          notify('Custom SQL cannot be empty for Rule ' + (i + 1), { type: 'warning' });
          return;
        }
        continue;
      }

      if (!r.fieldToUpdate || !r.sourceField) {
        notify('notifications.CLIENT_CLEANUP.RULES.MISSING_FIELDS', { type: 'warning', messageArgs: { index: i + 1 } });
        return;
      }
    }

    const cleanedRules = rules.map((r): CleanupRulePayload => {
      if (r.ruleEntryMode === 'custom_sql') {
        return {
          rule_entry_mode: 'custom_sql',
          custom_sql: r.customSql,
          field_to_update: 'location_number',
          source_field: 'custom_sql',
          split_mode: 'advanced',
        };
      }
      const length = Number(r.splitLength);
      if (r.mode === 'simple') {
        return {
          field_to_update: r.fieldToUpdate as CleanupTargetField,
          source_field: `orig_${r.sourceField}`,
          split_mode: 'simple',

          split_from: r.splitFrom,
          split_length: Number(r.splitLength),

          left_trim: r.splitFrom === 'left' ? length : 0,
          right_trim: r.splitFrom === 'right' ? length : 0,

          min_length: r.conditionLength !== '' && r.conditionLength !== null ? Number(r.conditionLength) : null,
          min_length_field: r.minLengthField ?? null,
          min_length_operator: r.minLengthOperator ?? '>=',

          match_value: null,

          only_if_target_empty: r.onlyIfTargetEmpty,
          also_update_medical: r.alsoUpdateMedical ?? false,
        };
      }
      if (r.mode === 'swap') {
        return {
          field_to_update: r.fieldToUpdate as CleanupTargetField,
          source_field: r.sourceField,
          split_mode: 'swap',

          match_value: r.matchValue?.trim() || null,
          match_field: r.matchValue ? (r.matchField as CleanupRulePayload['match_field']) : null,
          match_operator: r.matchValue ? r.matchOperator ?? null : null,

          min_length: r.conditionLength ? Number(r.conditionLength) : null,
          min_length_field: r.minLengthField ?? null,

          only_if_target_empty: r.onlyIfTargetEmpty,
        };
      }

      // advanced
      return {
        field_to_update: r.fieldToUpdate as CleanupTargetField,
        source_field: `orig_${r.sourceField}`,
        split_mode: 'advanced',

        left_trim: Number(r.leftTrim || 0),
        right_trim: Number(r.rightTrim || 0),
        trim_order: r.trimOrder,
        trim_action: r.trimAction ?? 'TAKE',

        min_length: r.conditionLength ? Number(r.conditionLength) : null,
        min_length_field: r.minLengthField ?? null,
        min_length_operator: r.minLengthOperator ?? '>=',

        match_value: r.matchValue || null,
        match_field: r.matchValue ? (r.matchField as CleanupRulePayload['match_field']) : null,
        match_operator: r.matchValue ? r.matchOperator ?? null : null,

        client_number: r.clientNumber ?? null,
        only_if_target_empty: r.onlyIfTargetEmpty,
        also_update_medical: r.alsoUpdateMedical ?? false,
      };
    });

    const payload: CleanupRequestPayload = {
      originalClientNumber: primaryClient.client_number,
      clientName: primaryClient.client_name,
      effectiveDate: effectiveDate,
      rules: cleanedRules,
    };

    onSaveRule(payload);
  };

  const applyTrimRule = (value: string, rule: CleanupRule, row: any) => {
    if (!value) return null;

    let result = value.trim();

    // SIMPLE MODE
    if (rule.mode === 'simple') {
      const len = Number(rule.splitLength);
      if (!len || len <= 0) return null;

      if (rule.conditionLength && rule.minLengthField) {
        const minLen = Number(rule.conditionLength);
        const lengthValue = row[rule.minLengthField];

        if (!lengthValue || String(lengthValue).length < minLen) {
          return null;
        }
      }

      return rule.splitFrom === 'right' ? result.slice(-len) : result.slice(0, len);
    }

    // ADVANCED MODE

    // Min length condition
    if (rule.conditionLength && rule.minLengthField) {
      const minLen = Number(rule.conditionLength);

      const lengthValue = row[rule.minLengthField];

      if (!lengthValue || String(lengthValue).trim().length < minLen) {
        return null;
      }
    }

    // Match condition
    if (rule.matchValue && rule.matchField && rule.matchOperator) {
      const matchSource = String(row[rule.matchField] ?? '').trim();
      let isMatch = false;

      switch (rule.matchOperator) {
        case 'contains':
          isMatch = matchSource.includes(rule.matchValue);
          break;
        case 'equals':
          isMatch = matchSource === rule.matchValue;
          break;
        case 'not_equals':
          isMatch = matchSource !== rule.matchValue;
          break;
      }

      if (!isMatch) return null;
    }

    const left = Number(rule.leftTrim || 0);
    const right = Number(rule.rightTrim || 0);
    const action = rule.trimAction || 'TAKE';

    if (rule.trimOrder === 'LR') {
      if (left > 0) {
        result = action === 'TAKE' ? result.slice(0, left) : result.slice(left);
      }

      if (right > 0) {
        result = action === 'TAKE' ? result.slice(-right) : result.slice(0, result.length - right);
      }
    } else {
      if (right > 0) {
        result = action === 'TAKE' ? result.slice(-right) : result.slice(0, result.length - right);
      }

      if (left > 0) {
        result = action === 'TAKE' ? result.slice(0, left) : result.slice(left);
      }
    }

    return result;
  };

  const handlePreview = () => {
    const hasCustom = rules.some((r) => r.ruleEntryMode === 'custom_sql');

    if (hasCustom) {
      notify('notifications.CLIENT_CLEANUP.PREVIEW.MANUAL_PREVIEW', { type: 'info' });
    }

    if (!listFilters) {
      notify('notifications.CLIENT_CLEANUP.PREVIEW.NO_PREVIEW_YET', { type: 'warning' });
      return;
    }

    if (listFilters) {
      dataProvider
        .getList('ClientCleanupPreview', {
          filter: listFilters,
          pagination: { page: 1, perPage: 1000 },
          sort: { field: 'id', order: 'ASC' },
        })
        .then((res) => {
          if (!rules.length) {
            setPreviewAfterTable(res.data);
            return;
          }
          const transformed = res.data.map((row) => applyRulesToRow(row, rules));
          setPreviewAfterTable(transformed);
        });
    }
  };

  const applyRulesToRow = (row: any, rules: CleanupRule[]) => {
    const updated = { ...row };

    rules.forEach((rule) => {
      if (rule.mode === 'swap') {
        if (rule.matchValue && rule.matchField && rule.matchOperator) {
          const matchSource = String(updated[rule.matchField] ?? '').trim();
          let isMatch = false;

          switch (rule.matchOperator) {
            case 'contains':
              isMatch = matchSource.includes(rule.matchValue);
              break;
            case 'equals':
              isMatch = matchSource === rule.matchValue;
              break;
            case 'not_equals':
              isMatch = matchSource !== rule.matchValue;
              break;
          }

          if (!isMatch) return;
        }

        if (rule.onlyIfTargetEmpty && updated[rule.fieldToUpdate]) {
          return;
        }
        const a = rule.fieldToUpdate;
        const b = rule.sourceField;

        // optional min-length guard
        if (rule.conditionLength) {
          const minLen = Number(rule.conditionLength);
          const aVal = updated[a];
          const bVal = updated[b];

          if (!aVal || !bVal || String(aVal).trim().length < minLen || String(bVal).trim().length < minLen) {
            return;
          }
        }

        const temp = updated[a];
        updated[a] = updated[b];
        updated[b] = temp;
        return;
      }
      const sourceValue = row[rule.sourceField];

      if (!sourceValue) return;

      if (rule.onlyIfTargetEmpty && updated[rule.fieldToUpdate]) {
        return;
      }

      if (rule.clientNumber && row.client_number !== rule.clientNumber) {
        return;
      }

      const newValue = applyTrimRule(sourceValue, rule, updated);
      if (newValue == null) return;

      if (
        (rule.mode === 'simple' || rule.mode === 'advanced') &&
        rule.fieldToUpdate === 'location_number' &&
        rule.sourceField === 'medical_group_number' &&
        rule.alsoUpdateMedical
      ) {
        const len = Number(rule.splitLength);
        const trimmed = sourceValue.trim();

        updated.location_number = trimmed.slice(-len);
        updated.medical_group_number = trimmed.slice(0, trimmed.length - len);
        return;
      }

      updated[rule.fieldToUpdate] = newValue;
    });

    return updated;
  };

  const isCleanupRuleValid = (rule: CleanupRule): boolean => {
    if (!rule.fieldToUpdate || !rule.sourceField || !rule.mode) {
      return false;
    }

    if (rule.mode === 'simple') {
      const len = Number(rule.splitLength);

      if (!rule.splitFrom || isNaN(len) || len <= 0) return false;

      // length condition requires field
      if (rule.conditionLength && !rule.minLengthField) return false;

      return true;
    }

    if (rule.mode === 'advanced') {
      const left = Number(rule.leftTrim || 0);
      const right = Number(rule.rightTrim || 0);

      // must have some trim
      if (left <= 0 && right <= 0) return false;

      if (!rule.trimAction) return false;

      // if length is provided, field is required
      if (rule.conditionLength && !rule.minLengthField) return false;

      if (rule.matchValue) {
        if (!rule.matchField || !rule.matchOperator) return false;
      }

      return true;
    }

    if (rule.mode === 'swap') {
      if (!rule.fieldToUpdate || !rule.sourceField) return false;

      const hasAnyMatch = !!rule.matchValue || !!rule.matchField || !!rule.matchOperator;

      if (hasAnyMatch) {
        if (!rule.matchValue || !rule.matchField || !rule.matchOperator) {
          return false;
        }
      }

      return true;
    }
    if (rule.alsoUpdateMedical && rule.fieldToUpdate !== 'location_number') {
      return false;
    }
    return false;
  };

  const isDraftRuleValid = (rule: CleanupRule): boolean => {
    if (ruleEntryMode === 'custom_sql') {
      return !!rule.customSql?.trim() && !validateCustomSql(rule.customSql);
    }

    return isCleanupRuleValid(rule);
  };

  const formatCleanupRuleSummary = (rule: CleanupRule): string => {
    if (rule.ruleEntryMode === 'custom_sql') {
      return `Custom SQL: ${rule.customSql?.substring(0, 50)}...`;
    }
    const targetLabel =
      rule.fieldToUpdate === 'medical_group_number'
        ? 'Medical'
        : rule.fieldToUpdate === 'location_number'
        ? 'Location'
        : 'Division';

    const sourceLabel =
      rule.sourceField === 'medical_group_number'
        ? 'Medical'
        : rule.sourceField === 'location_number'
        ? 'Location'
        : 'Division';

    let medicalNote = '';
    if (
      rule.alsoUpdateMedical &&
      rule.fieldToUpdate === 'location_number' &&
      rule.sourceField === 'medical_group_number'
    ) {
      medicalNote = ' | Medical = remainder';
    }

    // SIMPLE MODE
    if (rule.mode === 'simple') {
      const len = rule.splitLength || '-';
      const minLen = rule.conditionLength
        ? ` | If ${rule.minLengthField ?? 'source'} length ≥ ${rule.conditionLength}`
        : '';

      const clientCond = rule.clientNumber ? ` | Client = ${rule.clientNumber}` : '';

      return `${targetLabel} ← ${sourceLabel} | Simple | ${rule.splitFrom} ${len}${minLen}${medicalNote} | ${clientCond}`;
    }
    if (rule.mode === 'swap') {
      const conditions: string[] = [];

      if (rule.matchValue && rule.matchField && rule.matchOperator) {
        conditions.push(`${rule.matchField} ${rule.matchOperator} "${rule.matchValue}"`);
      }

      if (rule.conditionLength && rule.minLengthField) {
        conditions.push(`${fieldLabel(rule.minLengthField)} ${rule.minLengthOperator} ${rule.conditionLength}`);
      }

      const conditionText = conditions.length ? ` | If ${conditions.join(' and ')}` : '';

      return `${targetLabel} ← ${sourceLabel} | Swap${conditionText}`;
    }

    // ADVANCED MODE
    let trimText = '';

    if (rule.mode === 'advanced') {
      const actionLabel = rule.trimAction === 'REMOVE' ? 'Remove from' : 'Take from';

      const rightTrim = Number(rule.rightTrim);
      const leftTrim = Number(rule.leftTrim);

      if (!Number.isNaN(rightTrim) && rightTrim > 0) {
        trimText = `${actionLabel} Right ${rightTrim}`;
      } else if (!Number.isNaN(leftTrim) && leftTrim > 0) {
        trimText = `${actionLabel} Left ${leftTrim}`;
      }
    }

    const clientCond = rule.clientNumber ? ` | Client = ${rule.clientNumber}` : '';

    const conditions: string[] = [];
    if (rule.conditionLength && rule.minLengthField) {
      conditions.push(`${fieldLabel(rule.minLengthField)} ${rule.minLengthOperator} ${rule.conditionLength}`);
    }
    if (rule.matchValue) {
      conditions.push(`${rule.matchField} ${rule.matchOperator} "${rule.matchValue}"`);
    }

    const conditionText = conditions.length > 0 ? ` | If ${conditions.join(' and ')}` : '';

    return `${targetLabel} ← ${sourceLabel} | Advanced | ${trimText}${conditionText}${medicalNote} | ${clientCond}`;
  };

  useEffect(() => {
    clearTransformedPreview();
    setHasLoadedPreviewList(false);
  }, [primaryClient, effectiveDate]);

  useEffect(() => {
    setPreviewAfterTable([]);
  }, [rules]);

  useEffect(() => {
    if (!canUseRemainder && draftRule?.alsoUpdateMedical) {
      setDraftRule({
        ...draftRule,
        alsoUpdateMedical: false,
      });
    }
  }, [canUseRemainder, draftRule]);

  return (
    <Box sx={{ mt: 4 }}>
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Preview Data
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
          <TextField
            label="Client"
            value={`${primaryClient.client_name} (${primaryClient.client_number})`}
            variant="standard"
            size="small"
            disabled
            sx={{ width: 400 }}
          />
          <TextField
            label="Financial Cutoff Date (From)"
            type="date"
            value={financialCutoffDate}
            onChange={(e) => setFinancialCutoffDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            variant="standard"
            size="small"
            disabled={false}
            sx={{ width: 250 }}
          />
          <Button
            variant="contained"
            onClick={handleFetchPreview}
            startIcon={<SearchIcon />}
            sx={{ height: 40, mt: 1 }}
            disabled={!financialCutoffDate}
          >
            View Data
          </Button>
        </Box>

        {listFilters && (
          <Box sx={{ mt: 3 }}>
            <List
              resource="ClientCleanupPreview"
              filter={listFilters}
              pagination={<Pagination />}
              actions={false}
              empty={<CleanupEmpty />}
              sx={{ width: '100%', '& .RaList-content': { boxShadow: 'none' } }}
            >
              <Datagrid
                bulkActionButtons={false}
                size="small"
                sx={{
                  '& .MuiTableCell-head': { fontWeight: 'bold', fontSize: '0.9rem' },
                }}
              >
                <RaTextField source="client_name" label="Client Name" sortable={false} />
                <RaTextField source="medical_group_number" label="Medical Group Number" sortable={false} />
                <RaTextField source="location_number" label="Location Number" sortable={false} />
                <RaTextField source="division_number" label="Division Number" sortable={false} />
              </Datagrid>
            </List>
          </Box>
        )}
      </Paper>
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Preview Crosswalk Data
        </Typography>
        <ClientCrosswalkSummaryTable clientNumber={primaryClient.client_number} category="medical_groups" />
        <ClientCrosswalkSummaryTable clientNumber={primaryClient.client_number} category="locations" />
        <ClientCrosswalkSummaryTable clientNumber={primaryClient.client_number} category="divisions" />
      </Paper>
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Build Clean-up Rule
        </Typography>
        <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
          Define the UPDATE rule to be saved.
        </Typography>

        {rules.map((rule, index) => (
          <Paper
            key={index}
            sx={{
              p: 1.5,
              mb: 1,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Box>
              <Typography variant="body2">{formatCleanupRuleSummary(rule)}</Typography>
            </Box>

            <Box>
              <Button
                size="small"
                onClick={() => {
                  setDraftRule(rule);
                  setEditingRuleIndex(index);
                  setIsRuleDialogOpen(true);
                }}
              >
                Edit
              </Button>

              <Button size="small" color="error" onClick={() => setRules(rules.filter((_, i) => i !== index))}>
                Delete
              </Button>
            </Box>
          </Paper>
        ))}
        <Button
          variant="outlined"
          sx={{ mb: 2 }}
          onClick={() => {
            setRuleEntryMode('ui');

            setDraftRule({
              fieldToUpdate: '',
              sourceField: '',
              ruleEntryMode: 'ui',
              mode: 'simple',
              splitFrom: 'right',
              splitLength: '3',
              matchValue: '',
              matchField: undefined,
              matchOperator: undefined,
              conditionLength: '',
              leftTrim: '',
              rightTrim: '',
              minLengthField: 'location_number',
              minLengthOperator: '>=',
              trimOrder: 'LR',
              trimAction: 'TAKE',
              onlyIfTargetEmpty: false,
            });
            setEditingRuleIndex(null);
            setIsRuleDialogOpen(true);
          }}
        >
          + Add Rule
        </Button>
        <Button variant="contained" sx={{ ml: 2, mb: 2 }} onClick={handlePreview}>
          Preview Result
        </Button>

        {previewAfterTable.length > 0 && (
          <Paper sx={{ p: 2, mt: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Preview After Cleanup (All Rows)
            </Typography>

            <Datagrid
              resource="ClientCleanupPreview"
              data={previewAfterTable}
              total={previewAfterTable.length}
              isLoading={false}
              bulkActionButtons={false}
              size="small"
            >
              <RaTextField source="client_name" label="Client Name" />
              <RaTextField source="medical_group_number" label="Medical Group Number" />
              <RaTextField source="location_number" label="Location Number" />
              <RaTextField source="division_number" label="Division Number" />
            </Datagrid>
          </Paper>
        )}
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <Button
            variant="contained"
            sx={{ mt: 2 }}
            onClick={handleSave}
            disabled={
              isSaving || rules.length === 0 || !hasLoadedPreviewList || (hasUiRules && previewAfterTable.length === 0)
            }
          >
            Save Cleanup Rules
          </Button>
        </Box>
      </Paper>
      <Dialog open={isRuleDialogOpen} onClose={() => setIsRuleDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Add Cleanup Rule</DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
          <ToggleButtonGroup
            value={ruleEntryMode}
            exclusive
            sx={{ margin: 2 }}
            onChange={(_, v) => {
              if (!v) return;
              setRuleEntryMode(v);

              setDraftRule((prev) => {
                const base = prev ?? {
                  fieldToUpdate: '',
                  sourceField: '',
                  mode: 'simple',
                  ruleEntryMode: v,
                  onlyIfTargetEmpty: false,
                };

                if (v !== 'custom_sql') return base;

                const existing = (base as any).customSql?.trim();
                return {
                  ...base,
                  ruleEntryMode: 'custom_sql',
                  customSql: existing ? (base as any).customSql : DEFAULT_CUSTOM_SQL,
                };
              });
            }}
          >
            <ToggleButton value="ui">Rule Builder</ToggleButton>
            <ToggleButton value="custom_sql">Manual SQL</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {ruleEntryMode === 'ui' && (
          <DialogContent dividers>
            {draftRule && (
              <Box sx={{ display: 'grid', gap: 2 }}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle2" sx={{ mt: 2 }} gutterBottom>
                    Traget & Source
                  </Typography>
                  <FormControl variant="standard">
                    <InputLabel>Field to Clean</InputLabel>
                    <Select
                      value={draftRule.fieldToUpdate}
                      onChange={(e) => setDraftRule({ ...draftRule, fieldToUpdate: e.target.value })}
                    >
                      <MenuItem value="medical_group_number">Medical Group</MenuItem>
                      <MenuItem value="location_number">Location</MenuItem>
                      <MenuItem value="division_number">Division</MenuItem>
                    </Select>
                  </FormControl>

                  <FormControl variant="standard">
                    <InputLabel>Source Field</InputLabel>
                    <Select
                      value={draftRule.sourceField}
                      onChange={(e) => setDraftRule({ ...draftRule, sourceField: e.target.value })}
                    >
                      <MenuItem value="medical_group_number">Medical Group</MenuItem>
                      <MenuItem value="location_number">Location</MenuItem>
                      <MenuItem value="division_number">Division</MenuItem>
                    </Select>
                  </FormControl>

                  <div>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={draftRule.alsoUpdateMedical ?? false}
                          disabled={!canUseRemainder}
                          onChange={(e) =>
                            setDraftRule({
                              ...draftRule,
                              alsoUpdateMedical: e.target.checked,
                            })
                          }
                        />
                      }
                      label="Also update Medical Group using remainder"
                    />

                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={draftRule.onlyIfTargetEmpty}
                          onChange={(e) =>
                            setDraftRule({
                              ...draftRule,
                              onlyIfTargetEmpty: e.target.checked,
                            })
                          }
                        />
                      }
                      label="Only if target field is empty"
                    />
                  </div>
                </Paper>

                <Box sx={{ display: 'flex', gap: 2 }}>
                  <FormControl variant="standard">
                    <InputLabel>Mode</InputLabel>
                    <Select
                      value={draftRule.mode}
                      onChange={(e) => {
                        const mode = e.target.value as CleanupRule['mode'];

                        if (mode === 'swap') {
                          setDraftRule({
                            ...draftRule,
                            mode: 'swap',
                            matchValue: '',
                            matchField: undefined,
                            matchOperator: undefined,
                          });
                        } else if (mode === 'advanced') {
                          setDraftRule({
                            ...draftRule,
                            mode: 'advanced',
                            matchValue: '',
                            matchField: undefined,
                            matchOperator: undefined,
                          });
                        } else {
                          setDraftRule({
                            ...draftRule,
                            mode,
                          });
                        }
                      }}
                    >
                      <MenuItem value="simple">Simple</MenuItem>
                      <MenuItem value="advanced">Advanced</MenuItem>
                      <MenuItem value="swap">Swap</MenuItem>
                    </Select>
                  </FormControl>

                  <TextField
                    label="Client Number"
                    disabled={draftRule.mode === 'swap'}
                    value={draftRule.clientNumber || ''}
                    onChange={(e) =>
                      setDraftRule({
                        ...draftRule,
                        clientNumber: e.target.value,
                      })
                    }
                    variant="standard"
                  />
                </Box>
                {draftRule?.mode === 'simple' && (
                  <>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Typography variant="subtitle2" sx={{ mt: 2 }} gutterBottom>
                        Transformation
                      </Typography>

                      <FormControl variant="standard">
                        <InputLabel>Split From</InputLabel>
                        <Select
                          value={draftRule.splitFrom}
                          onChange={(e) =>
                            setDraftRule({ ...draftRule, splitFrom: e.target.value as 'left' | 'right' })
                          }
                        >
                          <MenuItem value="left">Left</MenuItem>
                          <MenuItem value="right">Right</MenuItem>
                        </Select>
                      </FormControl>

                      <TextField
                        label="Length"
                        value={draftRule.splitLength}
                        onChange={(e) => setDraftRule({ ...draftRule, splitLength: e.target.value })}
                        variant="standard"
                      />
                    </Paper>

                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Typography variant="subtitle2" sx={{ mt: 2 }} gutterBottom>
                        Apply only when
                      </Typography>
                      <Box
                        sx={{
                          border: '1px solid rgba(0,0,0,0.12)',
                          borderColor: 'divider',
                          borderRadius: 1,
                          p: 2,
                          mt: 1,
                        }}
                      >
                        <Typography variant="subtitle2">Length condition</Typography>
                        <TextField
                          label="Min Length"
                          value={draftRule.conditionLength}
                          onChange={(e) =>
                            setDraftRule({
                              ...draftRule,
                              conditionLength: e.target.value,
                              minLengthField: e.target.value ? draftRule.minLengthField : undefined,
                            })
                          }
                          variant="standard"
                        />

                        <FormControl variant="standard" disabled={!draftRule.conditionLength}>
                          <InputLabel>Min Length Operator</InputLabel>
                          <Select
                            value={draftRule.minLengthOperator || '>='}
                            onChange={(e) =>
                              setDraftRule({
                                ...draftRule,
                                minLengthOperator: e.target.value as CleanupRule['minLengthOperator'],
                              })
                            }
                          >
                            <MenuItem value=">=">≥</MenuItem>
                            <MenuItem value=">">&gt;</MenuItem>
                            <MenuItem value="<=">≤</MenuItem>
                            <MenuItem value="<">&lt;</MenuItem>
                            <MenuItem value="=">=</MenuItem>
                          </Select>
                        </FormControl>

                        <FormControl variant="standard" disabled={!draftRule.conditionLength}>
                          <InputLabel>Apply Min Length To</InputLabel>
                          <Select
                            value={draftRule.minLengthField || ''}
                            onChange={(e) =>
                              setDraftRule({
                                ...draftRule,
                                minLengthField: e.target.value as
                                  | 'medical_group_number'
                                  | 'location_number'
                                  | 'division_number',
                              })
                            }
                          >
                            <MenuItem value="medical_group_number">Medical Group</MenuItem>
                            <MenuItem value="location_number">Location</MenuItem>
                            <MenuItem value="division_number">Division</MenuItem>
                          </Select>
                        </FormControl>
                      </Box>
                    </Paper>
                  </>
                )}

                {draftRule?.mode === 'advanced' && (
                  <>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Transformation
                      </Typography>

                      <FormControl variant="standard">
                        <InputLabel>Trim Action</InputLabel>
                        <Select
                          value={draftRule.trimAction ?? 'TAKE'}
                          onChange={(e) =>
                            setDraftRule({
                              ...draftRule,
                              trimAction: e.target.value as 'TAKE' | 'REMOVE',
                            })
                          }
                        >
                          <MenuItem value="TAKE">Take Characters</MenuItem>
                          <MenuItem value="REMOVE">Remove Characters (remainder)</MenuItem>
                        </Select>
                      </FormControl>
                      {draftRule.trimAction === 'TAKE' && (
                        <FormControl variant="standard" disabled={!hasBothTrims}>
                          <InputLabel>Trim Order</InputLabel>
                          <Select
                            value={draftRule.trimOrder}
                            onChange={(e) => setDraftRule({ ...draftRule, trimOrder: e.target.value as 'LR' | 'RL' })}
                          >
                            <MenuItem value="LR">Left → Right</MenuItem>
                            <MenuItem value="RL">Right → Left</MenuItem>
                          </Select>
                        </FormControl>
                      )}

                      {draftRule.trimOrder === 'LR' ? (
                        <>
                          <TextField
                            label={leftLabel}
                            value={draftRule.leftTrim}
                            onChange={(e) => setDraftRule({ ...draftRule, leftTrim: e.target.value })}
                            variant="standard"
                          />

                          <TextField
                            label={rightLabel}
                            value={draftRule.rightTrim}
                            onChange={(e) => setDraftRule({ ...draftRule, rightTrim: e.target.value })}
                            variant="standard"
                          />
                        </>
                      ) : (
                        <>
                          <TextField
                            label={rightLabel}
                            value={draftRule.rightTrim}
                            onChange={(e) => setDraftRule({ ...draftRule, rightTrim: e.target.value })}
                            variant="standard"
                          />

                          <TextField
                            label={leftLabel}
                            value={draftRule.leftTrim}
                            onChange={(e) => setDraftRule({ ...draftRule, leftTrim: e.target.value })}
                            variant="standard"
                          />
                        </>
                      )}
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                        If only left or right is provided, only that side will be applied.
                      </Typography>
                    </Paper>

                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Typography variant="subtitle2" sx={{ mt: 3 }}>
                        Apply only when
                      </Typography>

                      <Box
                        sx={{
                          border: '1px solid rgba(0,0,0,0.12)',
                          borderColor: 'divider',
                          borderRadius: 1,
                          p: 2,
                          mt: 1,
                        }}
                      >
                        <Typography variant="subtitle2">Length condition</Typography>
                        <TextField
                          label="Min Length"
                          value={draftRule.conditionLength}
                          onChange={(e) =>
                            setDraftRule({
                              ...draftRule,
                              conditionLength: e.target.value,
                              minLengthField: e.target.value ? draftRule.minLengthField : undefined,
                            })
                          }
                          variant="standard"
                        />

                        <FormControl variant="standard" disabled={!draftRule.conditionLength}>
                          <InputLabel>Min Length Operator</InputLabel>
                          <Select
                            value={draftRule.minLengthOperator || '>='}
                            onChange={(e) =>
                              setDraftRule({
                                ...draftRule,
                                minLengthOperator: e.target.value as CleanupRule['minLengthOperator'],
                              })
                            }
                          >
                            <MenuItem value=">=">≥</MenuItem>
                            <MenuItem value=">">&gt;</MenuItem>
                            <MenuItem value="<=">≤</MenuItem>
                            <MenuItem value="<">&lt;</MenuItem>
                            <MenuItem value="=">=</MenuItem>
                          </Select>
                        </FormControl>

                        <FormControl variant="standard" disabled={!draftRule.conditionLength}>
                          <InputLabel>Apply Min Length To</InputLabel>
                          <Select
                            value={draftRule.minLengthField || ''}
                            onChange={(e) =>
                              setDraftRule({
                                ...draftRule,
                                minLengthField: e.target.value as
                                  | 'medical_group_number'
                                  | 'location_number'
                                  | 'division_number',
                              })
                            }
                          >
                            <MenuItem value="medical_group_number">Medical Group</MenuItem>
                            <MenuItem value="location_number">Location</MenuItem>
                            <MenuItem value="division_number">Division</MenuItem>
                          </Select>
                        </FormControl>
                      </Box>

                      <Box
                        sx={{
                          border: '1px solid rgba(0,0,0,0.12)',
                          borderColor: 'divider',
                          borderRadius: 1,
                          p: 2,
                          mt: 1,
                        }}
                      >
                        <Typography variant="subtitle2">Match condition</Typography>
                        <TextField
                          label="Match Value"
                          value={draftRule.matchValue ?? ''}
                          onChange={(e) =>
                            setDraftRule({
                              ...draftRule,
                              matchValue: e.target.value,
                              matchField: e.target.value ? draftRule.matchField : undefined,
                              matchOperator: e.target.value ? draftRule.matchOperator : undefined,
                            })
                          }
                          variant="standard"
                        />

                        <FormControl variant="standard">
                          <InputLabel>Match Applies To</InputLabel>
                          <Select
                            value={hasMatchValue ? draftRule.matchField : ''}
                            disabled={!hasMatchValue}
                            onChange={(e) =>
                              setDraftRule({
                                ...draftRule,
                                matchField: e.target.value as CleanupRule['matchField'],
                              })
                            }
                          >
                            <MenuItem value="medical_group_number">Medical Group</MenuItem>
                            <MenuItem value="location_number">Location</MenuItem>
                            <MenuItem value="division_number">Division</MenuItem>
                          </Select>
                        </FormControl>

                        <FormControl variant="standard">
                          <InputLabel>Match Operator</InputLabel>
                          <Select
                            value={hasMatchValue ? draftRule.matchOperator ?? '' : ''}
                            disabled={!hasMatchValue}
                            onChange={(e) =>
                              setDraftRule({
                                ...draftRule,
                                matchOperator: e.target.value as CleanupRule['matchOperator'],
                              })
                            }
                          >
                            <MenuItem value="contains">Contains</MenuItem>
                            <MenuItem value="equals">Equals</MenuItem>
                            <MenuItem value="not_equals">Does Not Equal</MenuItem>
                          </Select>
                        </FormControl>
                      </Box>
                    </Paper>
                  </>
                )}
                {draftRule?.mode === 'swap' && (
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle2" sx={{ mt: 2 }}>
                      Apply only when
                    </Typography>

                    <TextField
                      label="Match Value"
                      value={draftRule.matchValue}
                      onChange={(e) =>
                        setDraftRule({
                          ...draftRule,
                          matchValue: e.target.value,
                          matchField: e.target.value ? draftRule.matchField : undefined,
                          matchOperator: e.target.value ? draftRule.matchOperator : undefined,
                        })
                      }
                      variant="standard"
                    />

                    <FormControl variant="standard">
                      <InputLabel>Match Applies To</InputLabel>
                      <Select
                        value={hasMatchValue ? draftRule.matchField : ''}
                        disabled={!hasMatchValue}
                        onChange={(e) =>
                          setDraftRule({
                            ...draftRule,
                            matchField: e.target.value as CleanupRule['matchField'],
                          })
                        }
                      >
                        <MenuItem value="medical_group_number">Medical Group</MenuItem>
                        <MenuItem value="location_number">Location</MenuItem>
                        <MenuItem value="division_number">Division</MenuItem>
                      </Select>
                    </FormControl>

                    <FormControl variant="standard">
                      <InputLabel>Match Operator</InputLabel>
                      <Select
                        value={hasMatchValue ? draftRule.matchOperator ?? '' : ''}
                        disabled={!hasMatchValue}
                        onChange={(e) =>
                          setDraftRule({
                            ...draftRule,
                            matchOperator: e.target.value as CleanupRule['matchOperator'],
                          })
                        }
                      >
                        <MenuItem value="contains">Contains</MenuItem>
                        <MenuItem value="equals">Equals</MenuItem>
                        <MenuItem value="not_equals">Does Not Equal</MenuItem>
                      </Select>
                    </FormControl>

                    <TextField
                      label="Min Length"
                      value={draftRule.conditionLength || ''}
                      onChange={(e) =>
                        setDraftRule({
                          ...draftRule,
                          conditionLength: e.target.value,
                        })
                      }
                      variant="standard"
                    />
                  </Paper>
                )}
              </Box>
            )}
          </DialogContent>
        )}
        {ruleEntryMode === 'custom_sql' && (
          <DialogContent dividers sx={{ pt: 2 }}>
            <Alert
              severity="warning"
              variant="outlined"
              sx={{
                mb: 3,
              }}
            >
              <Typography variant="subtitle2" fontWeight={600}>
                Manual SQL Mode
              </Typography>
              <Typography variant="body2">
                Only single <strong>UPDATE</strong> statements using the <strong>{'{{TABLE}}'}</strong> placeholder are
                permitted. This rule bypasses automated validations. Use with caution.
              </Typography>
            </Alert>
            <Typography
              variant="caption"
              sx={{ display: 'block', mb: 1, color: 'text.secondary', fontStyle: 'italic' }}
            >
              Tip: Your query should look like: UPDATE {'{{TABLE}}'} SET field = 'new_value' WHERE ...
            </Typography>
            <Box sx={{ mt: 1 }}>
              <TextField
                label="Custom SQL"
                multiline
                minRows={10}
                fullWidth
                variant="outlined"
                value={draftRule?.customSql || ''}
                error={!!customSqlError}
                helperText={customSqlError || ' '}
                onChange={(e) => setDraftRule((prev) => (prev ? { ...prev, customSql: e.target.value } : prev))}
                placeholder={`UPDATE billing_stg.member_summary
SET location_number = ...
WHERE client_number = '12345'`}
                sx={{
                  fontFamily: 'monospace',
                  '& textarea': {
                    fontSize: '0.9rem',
                    lineHeight: 1.6,
                  },
                }}
              />
            </Box>
          </DialogContent>
        )}
        {draftRule && !isDraftRuleValid(draftRule) && (
          <Typography variant="caption" color="error" sx={{ px: 2, pb: 1 }}>
            Please complete all required fields for the selected mode.
          </Typography>
        )}
        <DialogActions>
          <Button onClick={() => setIsRuleDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!draftRule || !isDraftRuleValid(draftRule)}
            onClick={() => {
              if (!draftRule) return;

              setRules((prev) => {
                if (editingRuleIndex === null) {
                  return [...prev, draftRule];
                }
                const copy = [...prev];
                copy[editingRuleIndex] = draftRule;
                return copy;
              });

              setIsRuleDialogOpen(false);
              setDraftRule(null);
            }}
          >
            Save Rule
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClientCleanupForm;
